// @flow

import * as React from 'react';
import { bind } from '@ta/common.api';
import { featureIsEnabled } from '@ta/common.features';
import classNames from '@ta/common.classnames';
import DataCacher from '@ta/common.data-cacher';
import GlobalNavContext from '@ta/brand.global-nav-context';
import GeoPill from '@ta/brand.global-nav-geopill';
import ShoppingCart from '@ta/brand.global-nav-action-shopping-cart';
import InboxTrigger from '@ta/brand.global-nav-action-inbox';
import { Profile } from '@ta/brand.global-nav-action-profile';
import * as Import from '@ta/platform.import';
import WindowScroll from '@ta/events.window-scroll';
import { QuickLinksLite } from '@ta/brand.quick-links';
import Radio from '@ta/common.radio';
import NavLinks from './GlobalNavLinks';
import styles from './GlobalNav.less';
import GlobalNavBar from './GlobalNavBar';
import Logo from './Logo';
import SideBar from './SideBar';
import T4BNav, { t4bNavPages } from './T4BNav';

const SatelliteGeoPill = Import.component.onUsage(() =>
  import('@ta/brand.global-nav-satellite-geopill'),
);

export type NavLinksDisplayType =
  | 'DEFAULT' // The regular links we'd usually see on pages other than home post-social launch (e.g., from the 2017 rebrand)
  | 'QUICK_LINKS_LITE' // The version of quick links that show in the nav after scrolling past the quick link tiles.
  | 'T4B' // Management Center nav links (see T4BNav)
  | 'NONE' // Some servlets don't show any nav links
  | 'DEFAULT_WITH_SEO_LINKS'; // New version of the links with SEO sublinks

type SupportedLineNums = 1 | 2;

/** The props for the GlobalNav component */
type Props = {|
  /** True if the api call is still loading */
  loading: boolean,

  /** Whether or not the api call returned an error */
  error: ?string,

  /** Whether or not this is an obsolete browser (Internet Explorer version 10.0 or lower) */
  isObsoleteBrowser: boolean,

  /** Whether there's a hero image on the page */
  hasHeroImage: boolean,

  /** Whether or not the we are at a mobile breakpoint */
  isMobileBreakpoint: boolean,

  /** Whether or not we're on a nearby-scoped page */
  isNearby: boolean,

  /** Whether or not the header should display the geopill */
  showGeopill: boolean,

  /** The number of lines the header has (home page has one line, hotels has two lines) */
  numLines: SupportedLineNums,

  /** Whether or not the header should be persistent on the page */
  isPersistentHeader: boolean,

  /** Display type for nav links (default, quick links lite, or none) */
  navLinksDisplayType: NavLinksDisplayType,

  /** Icons stay on the header as long as they can (on mobile/geopill pages they scroll down with the top of the window)  */
  usePersistentIcons: boolean,

  /** Whether or not to make adjusts for the smart banner */
  adjustForSmartBanner: boolean,

  /** We hide the logo when we enter a page with a mobile page with a geopill internally */
  hideLogo: boolean,

  /** Whether we show a very simple header with just the logo and search icon. */
  liteHeader: boolean,

  /** The current page name */
  page: string,

  /** The current scoped geo */
  geo: ?number,
|};

/** The state for the GlobalNav component */
type State = {|
  /** Whether or not the header should stay fixed at the top of the page */
  scrolledPastTop: boolean,

  /** Whether or not the page is currently displaying a smart banner */
  hasSmartBanner: boolean,

  /** Number of pixels high the nav currently is. */
  height: number,

  /**
   * Since the height comes from a ref and those are seemingly still unset on mount, we need a flag for componentDidUpdate only set height once
   * (as opposed to infinite looping).
   */
  heightInitiallySet: boolean,
|};

/** Determines which classes the GlobalNav should have. */
const getClasses = (
  isPersistentHeader: boolean,
  adjustForSmartBanner: boolean,
  hasSmartBanner: boolean,
  isMobile: boolean,
  scrolledPastTop: boolean,
  hasHeroImage: boolean,
): string =>
  classNames({
    [styles.globalNavHome]: hasHeroImage,
    [styles.globalNavScrolling]: !isPersistentHeader && isMobile,
    [styles.smartBannerTop]: adjustForSmartBanner && hasSmartBanner,
    [styles.fixed]: isPersistentHeader && scrolledPastTop,
  });

/** The global nav component */
class GlobalNav extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      scrolledPastTop: false,
      hasSmartBanner: !props.loading && !props.error ? props.adjustForSmartBanner : false,
      height: 0,
      heightInitiallySet: false,
    };
    this.topDiv = React.createRef();
    this.heightDiv = React.createRef();
  }

  componentDidMount() {
    /** Listen to this radio channel which is emitted from: site/placements/mobile_smart_banner/handler.js */
    Radio('global-nav-component').on('smart-banner-did-close', this.smartBannerDidClose);

    if (this.props.hideLogo) {
      // Commenting out the scrollTo until WP-631 is fixed because mount happens pretty late during page load which results in bad UX.
      // // eslint-disable-next-line no-restricted-globals
      // window.scrollTo(0, 50);
    }

    // Check scroll in case they ended up mid-page before mount, especially from refreshing / clicking back.
    this.handleScroll();
  }

  componentDidUpdate() {
    if (!this.state.heightInitiallySet) {
      // Set height in state on page load.
      this.setHeight();

      // We need state.height set on page load, and we're being careful as to not infinite loop via setState here.
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ heightInitiallySet: true });
    }
  }

  /** Update the height of the global nav in the state so if it's changed via scrolling we can update anything depending on it. */
  setHeight = () => {
    const { current } = this.heightDiv;
    this.setState({ height: current ? current.clientHeight : 0 });
  };

  /** A reference to the top div of this component. I.e., the div that should be touching the top of the page. */
  topDiv: any;

  /**
   * A ref to the element that immediately wraps the GlobalNavBar component. Needs to be separate from topDiv because when
   * the header is fixed topDiv's height is 0.
   */
  heightDiv: any;

  /** If the user has scrolled down the page, then calculate if the header should be fixed (if it is a persistent page) */
  handleScroll = () => {
    const { current } = this.topDiv;
    /** Check if this state is different from the previous state */
    /* eslint-disable no-restricted-globals */
    if (
      current &&
      window.pageYOffset > current.getBoundingClientRect().top + window.pageYOffset !==
        this.state.scrolledPastTop
    ) {
      this.setState({
        scrolledPastTop: !this.state.scrolledPastTop,
      });
    }
    /* eslint-enable no-restricted-globals */
  };

  /** When the smart banner closes update the state */
  smartBannerDidClose = () =>
    this.setState({
      hasSmartBanner: false,
    });

  render() {
    const navClasses = getClasses(
      this.props.isPersistentHeader && featureIsEnabled('persistent_header_unscoped_bu_home'),
      this.props.adjustForSmartBanner,
      this.state.hasSmartBanner,
      this.props.isMobileBreakpoint,
      this.state.scrolledPastTop,
      this.props.hasHeroImage,
    );

    // We're hard-coding this because otherwise we get some jumpiness once we start scrolling and go into fixed mode.
    // add the height for nav links for desktop when we enable global_nav_links_test - NAV-8422
    let topDivHeight;
    if (this.props.hasHeroImage) {
      let topDivHeightDesktop = 60;
      const topDivHeightMobile = 50;

      if (
        featureIsEnabled('global_nav_links_test') &&
        !featureIsEnabled('breadcrumbs_as_global_nav')
      ) {
        topDivHeightDesktop += 45;
      }

      topDivHeight = this.props.isMobileBreakpoint ? topDivHeightMobile : topDivHeightDesktop;
    }

    return (
      <GlobalNavContext.Provider
        value={{
          numLines: this.props.numLines,
          usePersistentIcons: this.props.usePersistentIcons,
        }}
      >
        <div ref={this.topDiv} style={{ height: topDivHeight }}>
          <div className={navClasses}>
            <div ref={this.heightDiv}>
              <GlobalNavBar
                servletName={this.props.page}
                navLinksDisplayType={this.props.navLinksDisplayType}
                isObsoleteBrowser={this.props.isObsoleteBrowser}
                navHeight={this.state.height}
                hasGeopill={this.props.showGeopill}
                displayMultiLine={this.props.numLines >= 2}
                liteHeader={this.props.liteHeader}
                isStickyHeader={this.props.isPersistentHeader}
              />
            </div>
          </div>
        </div>
        <WindowScroll
          callback={() => {
            this.handleScroll();
            this.setHeight();
          }}
        />
      </GlobalNavContext.Provider>
    );
  }
}

/** Function to get the parameters for the GlobalNav api call */
const getHeaderParams = state => {
  const { initialServletName, device } = state.meta;
  const viewportCategory = state.meta.forceFullSite ? 'DESKTOP' : device.viewportCategory;

  const paramMap = {
    servlet: initialServletName,
    viewportCategory,
    geoId: state.route.geo ? state.route.geo : null,
    pageName: state.route.page,
    isTaReferrer: state.meta.isTaReferrer ? 'true' : 'false',
    latitude: state.route.latitude,
    longitude: state.route.longitude,
  };

  const filteredParams = {};

  Object.keys(paramMap).forEach(key => {
    if (paramMap[key] !== null && typeof paramMap[key] !== 'undefined') {
      filteredParams[key] = paramMap[key];
    }
  });

  return new URLSearchParams(filteredParams);
};

const noNavLinksPages = [
  'AddListing',
  'RegistrationController',
  'Search',
  'UpdateListing',
  'UserReview',
  'UserReviewEdit',
  'UserReviewEdit',
];

/** Makes the global nav api request, loads the logo, and passes props into the GlobalNav component */
export default bind<Props, {||}, _, _, _, _>((state, stage) => {
  const headerApiCall = stage(`/data/1.0/brand/header?${getHeaderParams(state).toString()}`);
  Logo.stage(state, stage);
  NavLinks.stage(state, stage);
  QuickLinksLite.stage(state, stage);
  GeoPill.stage(state, stage);
  (SatelliteGeoPill: any).stage(state, stage);
  if (featureIsEnabled('shopping_cart_desktop')) {
    ShoppingCart.stage(state, stage);
  }
  Profile.stage(state, stage);
  InboxTrigger.stage(state, stage);
  SideBar.stage(state, stage);
  T4BNav.stage(state, stage);

  return newState => {
    const response = headerApiCall.get();
    const obsoleteBrowser =
      newState.meta.device.browser.family === 'IE' &&
      newState.meta.device.browser.majorVersion <= 10;
    // Determine if we are in the BU home lander, not real /Home page
    const isBUHomeLander =
      newState.route.page === 'Home' &&
      newState.meta.initialServletName !== 'Home' &&
      newState.meta.initialServletName !== 'Tourism' &&
      newState.meta.initialServletName !== 'MobileTourism';

    let navLinksDisplayType: NavLinksDisplayType;
    if (newState.route.page === 'Home') {
      if (featureIsEnabled('global_nav_links_test') || isBUHomeLander) {
        navLinksDisplayType = 'DEFAULT_WITH_SEO_LINKS';
      } else {
        navLinksDisplayType = 'QUICK_LINKS_LITE';
      }
    } else if (t4bNavPages.includes(newState.route.page)) {
      navLinksDisplayType = 'T4B';
    } else if (
      noNavLinksPages.includes(newState.route.page) ||
      featureIsEnabled('legacy_nav_links')
    ) {
      navLinksDisplayType = 'NONE';
    } else {
      navLinksDisplayType = 'DEFAULT';
    }

    let apiData = {};
    if (response.data) {
      apiData = {
        showGeopill: response.data.showGeopill,
        numLines: response.data.numLines,
        // If usePersistentIcons is true it's probably because we're on a lander. But on mobile we only want to show it as persistent if we're on the "real" homepage (i.e., no other landers).
        isPersistentHeader:
          response.data.isPersistentHeader &&
          !(
            newState.meta.device.viewportCategory === 'MOBILE' &&
            newState.meta.initialServletName !== 'Home' &&
            newState.meta.initialServletName !== 'MobileTourism'
          ),
        navLinksDisplayType,
        usePersistentIcons: response.data.usePersistentIcons,
        adjustForSmartBanner: response.data.adjustForSmartBanner,
        hideLogo: response.data.hideLogo,
        liteHeader: response.data.liteHeader,
      };
    }

    return {
      loading: response.loading,
      hasHeroImage: newState.route.page === 'Home',
      isMobileBreakpoint: newState.meta.device.viewportCategory === 'MOBILE',
      isNearby: !!newState.route.isNearby,
      error: response.error,
      isObsoleteBrowser: obsoleteBrowser,
      page: newState.route.page === 'Home' ? newState.meta.initialServletName : newState.route.page,
      geo: +newState.route.geo || null,
      ...apiData,
    };
  };
})(DataCacher()(GlobalNav));
